import { usePagedLoad } from '@littlebird/ui-utils';
import { useEffect, useRef } from 'react';

function findScrollParent(from: HTMLElement | null): Element | null {
  let el = from?.parentElement;
  while (el) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      el.scrollHeight > el.clientHeight + 1
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

type Options = {
  hasMorePages: boolean;
  loadNextPage: () => Promise<void>;
  enabled?: boolean;
};

export function useScrollParentSentinelLoadMore({
  hasMorePages,
  loadNextPage,
  enabled = true,
}: Options) {
  const { runLoadMore, isLoadingMore } = usePagedLoad({
    enabled,
    canLoadMore: hasMorePages,
    loadPage: loadNextPage,
  });
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled || !hasMorePages) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const root = findScrollParent(sentinel);
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void runLoadMore();
      },
      { root: root ?? undefined, rootMargin: '240px', threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [enabled, hasMorePages, runLoadMore]);

  return { sentinelRef, isLoadingMore };
}
